import React, { memo, useState, useContext } from 'react';
import { Theme } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import classNames from 'classnames';
import EDTableRow from '../../../../../../../../../BaseComponents/Table/Components/EDTableRow';
import EDTableCell from '../../../../../../../../../BaseComponents/Table/Components/EDTableCell';
import {
  IRapportUtdelning,
  IGrundurval,
} from '../../../../Queries/AnalysutdelningQuery/AnalysutdelningQuery';
import {
  ChangeVisibilityFnType,
  SetStartAnalysFnType,
  ChangeUrvalFnType,
  SetAvailableFnType,
} from '../../../AnalysUtdelningMain/AnalysUtdelningMain';
import AnalysUtdelningSettingButton from '../AnalysUtdelningSettingButton/AnalysUtdelningSettingButton';
import AnalysUtdelningTableRowUrvalDialog from '../AnalysUtdelningTableRowUrvalDialog/AnalysUtdelningTableRowUrvalDialog';
import AnalysUtdelningGrundurvalDisplay from '../AnalysUtdelningGrundurvalDisplay/AnalysUtdelningGrundurvalDisplay';
import { KodplanContext } from '../../../../../../../../../Components/KodplanManager/Context/KodplanContext';

interface IProps {
  analys: IRapportUtdelning;
  setStartAnalys: SetStartAnalysFnType;
  changeUrval: ChangeUrvalFnType;
  changeVisibility: ChangeVisibilityFnType;
  urvalOptions: IGrundurval[];
  setAvailable: SetAvailableFnType;
  classes?: any;
}

const styles = (
  theme: Theme,
  _params: any,
  classes: Record<'row', string>
) => ({
  row: {
    zIndex: 90,
  },
  visibleOnHover: {
    opacity: 0,
    transition: 'opacity 100ms ease',
    [`.${classes.row}:hover &`]: {
      opacity: 1,
    },
  },
  clickable: {
    cursor: 'pointer',
  },
  disabled: {
    backgroundColor: theme.palette.grey[500],
  },
  tableCell: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingRight: '20px',
  },
});

/**
 * Komponent för en rad i analysutdelningstabellen
 */
export const AnalysUtdelningRow: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const {
    analys,
    setStartAnalys,
    changeVisibility,
    classes,
    changeUrval,
    urvalOptions,
    setAvailable,
  } = props;

  const kodplanContext = useContext(KodplanContext);
  const showObligatoriskGas = kodplanContext.activeKodplan?.gas ?? false;
  const showObligatoriskEjGas = !showObligatoriskGas;

  const [urvalDialogOpen, setUrvalDialogOpen] = useState(false);

  const startDisabled =
    analys.startAnalysForNewUser || !analys.availableForAnalys;

  const onGrundUrvalChange = (grundUrvalId: string) => {
    if (grundUrvalId === 'ADD_NEW') {
      setUrvalDialogOpen(true);
    } else {
      changeUrval(analys, grundUrvalId);
    }
  };

  return (
    <>
      <EDTableRow
        id={analys.startAnalysForNewUser ? 'start-analys-element' : undefined}
        className={classes.row}
      >
        <EDTableCell className={classes.tableCell}>
          {analys.category}
        </EDTableCell>
        <EDTableCell className={classes.tableCell}>{analys.name}</EDTableCell>
        <EDTableCell className={classes.tableCell}>
          <AnalysUtdelningSettingButton
            label={analys.availableForAnalys ? 'Tillgänglig' : 'Ej tillgänglig'}
            value={analys.availableForAnalys}
            disabled={analys.startAnalysForNewUser}
            disabledReason={
              analys.startAnalysForNewUser
                ? 'Startanalys måste vara tillgänglig'
                : undefined
            }
            tooltip="Markera som tillgänglig för användare i Ekonomisk analys"
            onClick={available => setAvailable(analys, available)}
            icon={analys.availableForAnalys ? 'explore' : 'explore_off'}
          />
        </EDTableCell>
        <EDTableCell className={classes.tableCell}>{analys.type}</EDTableCell>
        {showObligatoriskGas && (
          <EDTableCell className={classes.tableCell}>
            {analys.obligatoriskGas.map(role => role.name).join(', ')}
          </EDTableCell>
        )}
        {showObligatoriskEjGas && (
          <EDTableCell className={classes.tableCell}>
            {analys.obligatoriskEjGas ? 'Ja' : 'Nej'}
          </EDTableCell>
        )}
        <EDTableCell className={classes.tableCell}>
          {analys.canCombineWith}
        </EDTableCell>
        <EDTableCell className={classes.tableCell}>
          <AnalysUtdelningGrundurvalDisplay
            urvalOptions={urvalOptions}
            analys={props.analys}
            onGrundUrvalChange={onGrundUrvalChange}
          />
        </EDTableCell>
        <EDTableCell className={classes.tableCell}>
          <AnalysUtdelningSettingButton
            onClick={visible => changeVisibility(analys, visible)}
            value={analys.defaultForNewUser}
            disabled={
              analys.startAnalysForNewUser || !analys.availableForAnalys
            }
            disabledReason={
              !analys.availableForAnalys
                ? 'Analysen måste vara tillgänglig i Ekonomisk Analys'
                : 'Startanalys måste vara synlig'
            }
            tooltip="Markera som synlig eller ej i analysväljaren för nya användare"
            label={analys.defaultForNewUser ? 'Synlig' : 'Ej synlig'}
            icon={analys.defaultForNewUser ? 'visibility' : 'visibility_off'}
          />
        </EDTableCell>
        <EDTableCell className={classes.tableCell}>
          <span
            className={classNames({
              [classes.visibleOnHover]: !analys.startAnalysForNewUser,
            })}
          >
            <AnalysUtdelningSettingButton
              onClick={() => setStartAnalys(analys)}
              noDisabledColor={startDisabled && analys.availableForAnalys}
              disabled={startDisabled}
              disabledReason={
                !analys.availableForAnalys
                  ? 'Analysen måste vara tillgänglig i Ekonomisk Analys'
                  : 'Det måste finnas en startanalys. Denna analys kommer avmarkeras om du väljer en annan startanalys'
              }
              tooltip="Markera som startanalys för nya användare"
              label="Start"
              value={analys.startAnalysForNewUser}
              icon={analys.startAnalysForNewUser ? 'star' : 'star_border'}
            />
          </span>
        </EDTableCell>
      </EDTableRow>
      <AnalysUtdelningTableRowUrvalDialog
        open={urvalDialogOpen}
        setOpen={setUrvalDialogOpen}
        analys={analys}
        changeUrval={changeUrval}
      />
    </>
  );
};

export default withStyles(memo(AnalysUtdelningRow), styles);
